import { getAdminApiBaseUrl } from '@/lib/adminApi';

const API_BASE_URL = getAdminApiBaseUrl();

export interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  /**
   * Persisted reference to the avatar object in S3. For native M1 this
   * is the S3 key (e.g. `profiles/<sanitizedUserId>/<uuid>.<ext>`); for
   * legacy/Auth0 fallbacks it may be a fully-qualified URL. NEVER store
   * a presigned URL here — they expire and would brick the avatar.
   */
  avatar: string | null;
  /**
   * Short-lived presigned GET URL the browser can render directly,
   * emitted by `GET /profile` (and the `PUT /profile` response) when
   * `avatar` is an S3 key the backend can sign. Optional for backward
   * compatibility with stacks that haven't deployed the presign helper
   * yet; consumers should prefer this field over rebuilding the URL
   * from `avatar` when present. Never persisted.
   */
  avatarUrl?: string | null;
}

/**
 * Get user profile from API
 */
export async function getProfile(getToken: () => Promise<string>): Promise<ProfileData | null> {
  try {
    const token = await getToken();
    const response = await fetch(`${API_BASE_URL}/profile`, {
      method: 'GET',
      credentials: 'omit',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Profile doesn't exist yet, return null
        return null;
      }
      throw new Error(`Failed to fetch profile: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.success && result.data) {
      return {
        firstName: result.data.firstName || '',
        lastName: result.data.lastName || '',
        email: result.data.email || '',
        phone: result.data.phone || '',
        role: result.data.role || '',
        avatar: result.data.avatar || null,
        avatarUrl: result.data.avatarUrl ?? null,
      };
    }
    return null;
  } catch (error: any) {
    console.error('Error fetching profile:', error);
    throw error;
  }
}

/**
 * Get presigned URL for uploading profile picture
 */
export async function getProfileUploadUrl(
  contentType: string,
  getToken: () => Promise<string>
): Promise<{ uploadUrl: string; key: string; bucket: string }> {
  try {
    const token = await getToken();
    const response = await fetch(`${API_BASE_URL}/profile/upload-url`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ contentType }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get upload URL: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.success && result.data) {
      return {
        uploadUrl: result.data.uploadUrl,
        key: result.data.key,
        bucket: result.data.bucket,
      };
    }
    throw new Error('Invalid response from server');
  } catch (error: any) {
    console.error('Error getting upload URL:', error);
    throw error;
  }
}

/**
 * Upload file to S3 using presigned URL
 */
export async function uploadToS3(
  file: File,
  uploadUrl: string
): Promise<void> {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload to S3: ${response.statusText}`);
    }
  } catch (error: any) {
    console.error('Error uploading to S3:', error);
    throw error;
  }
}

/**
 * Get S3 URL for a profile picture key
 */
export function getProfilePictureUrl(key: string, stage: 'staging' | 'production'): string {
  const bucket = `${stage}-krosskast-profile-pictures`;
  return `https://${bucket}.s3.${process.env.REACT_APP_AWS_REGION || 'us-east-1'}.amazonaws.com/${key}`;
}

/**
 * Update user profile via API
 */
export async function updateProfile(
  profileData: Partial<ProfileData>,
  getToken: () => Promise<string>
): Promise<ProfileData> {
  try {
    const token = await getToken();
    
    // Validate token before making request
    if (!token || token.trim() === '') {
      throw new Error('No authentication token available. Please refresh the page and try again.');
    }
    
    // Log what we're sending for debugging
    console.log('Updating profile with data:', {
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      phone: profileData.phone,
      hasAvatar: !!profileData.avatar,
      apiUrl: `${API_BASE_URL}/profile`,
      tokenPresent: !!token,
    });
    
    const response = await fetch(`${API_BASE_URL}/profile`, {
      method: 'PUT',
      credentials: 'omit',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phone: profileData.phone,
        role: profileData.role,
        avatar: profileData.avatar,
      }),
    });

    // Handle CORS/403 errors specifically
    if (response.status === 403) {
      const errorText = await response.text().catch(() => '');
      console.error('CORS/Authorization error (403):', {
        errorText,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });
      throw new Error('Authorization failed. Please refresh the page and try again.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Profile update error:', {
        status: response.status,
        statusText: response.statusText,
        errorData,
        headers: Object.fromEntries(response.headers.entries())
      });
      throw new Error(errorData.message || `Failed to update profile: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.success && result.data) {
      return {
        firstName: result.data.firstName || '',
        lastName: result.data.lastName || '',
        email: result.data.email || '',
        phone: result.data.phone || '',
        role: result.data.role || '',
        avatar: result.data.avatar || null,
        avatarUrl: result.data.avatarUrl ?? null,
      };
    }
    throw new Error('Invalid response from server');
  } catch (error: any) {
    console.error('Error updating profile:', error);
    throw error;
  }
}

